/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.15.0
 */

import { Fragment, useCallback } from 'react';

import isArray from 'lodash/isArray';
import { useSelector } from 'react-redux';

import { LinkBlock } from '@components/common';
import { ELocaleKeys } from '@modules/internationalization/intConstants';
import {
    makeSelectPageOrderIdTranslation,
    makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import type { IPageOrderIdTranslation } from '@modules/internationalization/types';
import { ECalendarPeriod } from '@modules/internationalization/types/common/calendar';
import { makeSelectGuardComponents } from '@modules/role';
import declination from '@utils/declination';
import { formatDate } from '@utils/formatText';
import { stringFormatParams } from '@utils/stringFormat';

import {
    getPageAutoReportIdUrl,
    isPageAutoReportIdUrlExternal,
} from '../../PageAutoReportId/utils/getPageAutoReportIdUrl';
import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import {
    EOrderIdParameterType,
    IOrderIdInfo,
    IOrderIdParameterAutoReport,
    IOrderIdParameterCommon,
    IOrderIdParameterDate,
    IOrderIdParameterDatePeriod,
    IOrderIdParameterLink,
    IOrderIdParameterList,
    TOrderIdParamType,
} from '../types';

const OrderIdParameters = ({
    parameters,
}: Pick<IOrderIdInfo, 'parameters'>) => {

    const componentsFeature = useSelector(makeSelectGuardComponents());
    const translationCommon = useSelector(makeSelectTranslationCommon());
    const translationOrderId = useSelector(makeSelectPageOrderIdTranslation());

    const renderText = useCallback((parameter: IOrderIdParameterCommon) =>
        parameter.value, []);

    const renderDate = useCallback((parameter: IOrderIdParameterDate) =>
        (parameter.isCurrent && translationOrderId.parameters.date.isCurrent)
        || (parameter.value && formatDate(parameter.value, { skipZone: true })), [translationOrderId]);

    const renderDatePeriod = useCallback((parameter: IOrderIdParameterDatePeriod) =>
        `c ${formatDate(parameter.valueFrom)} по ${formatDate(parameter.valueTo)}`, []);

    const renderLink = useCallback((parameter: IOrderIdParameterLink) =>
        <LinkBlock
            isExternalUrl={parameter.isExternal}
            url={parameter.id}
        >
            {parameter.value}
        </LinkBlock>, []);

    const renderAutoReport = useCallback((parameter: IOrderIdParameterAutoReport) => {
        const periodType = translationCommon.calendar.period[parameter.periodType] || '';
        let periodDay = '';
        if (parameter.periodType === ECalendarPeriod.W) {
            periodDay = `${declination(parameter.dayOfPeriod, translationOrderId.parameters.autoReport.day)}
            ${translationCommon.calendar.weeks[parameter.dayOfPeriod - 1]}`;
        } else if (parameter.periodType === ECalendarPeriod.M) {
            periodDay = stringFormatParams(translationOrderId.parameters.autoReport.month, {
                [ELocaleKeys.date]: parameter.dayOfPeriod,
            });
        }
        const time = stringFormatParams(translationOrderId.parameters.autoReport.time, {
            [ELocaleKeys.date]:
                formatDate(parameter.startTime, { format: EFormatData.OnlyHourMinutes }),
        });
        if (parameter.delDate) {
            const delDate = stringFormatParams(translationOrderId.parameters.autoReport.deleteDay, {
                [ELocaleKeys.date]:
                    formatDate(parameter.delDate, { format: EFormatData.FullDateWithTime }),
            });
            return <Fragment>
                <div> {periodType} {periodDay} {time} </div>
                <div> {delDate}</div>
            </Fragment>;
        } else {
            return renderLink({
                id: getPageAutoReportIdUrl(componentsFeature, parameter.reportId),
                isExternal: isPageAutoReportIdUrlExternal(componentsFeature),
                type: EOrderIdParameterType.link,
                value: `${periodType} ${periodDay} ${time}`,
            });
        }
    }, [componentsFeature, translationCommon, translationOrderId, renderLink]);

    const renderList = useCallback((parameter: IOrderIdParameterList) => isArray(parameter?.value)
        && <Fragment>
            {
                parameter.value.map(param => <div>
                    {param.type === EOrderIdParameterType.link
                        ? renderLink(param as IOrderIdParameterLink)
                        : renderText(param)}
                </div>,
                )
            }
        </Fragment>, [renderLink, renderText]);

    const renderParameter = useCallback(([paramKey, paramValue]: [string, TOrderIdParamType | undefined]) => {
        if (!paramValue) return;

        const type = paramValue.type || EOrderIdParameterType.text;
        return (
            <div className="order__block">
                <div className="order__block-label order__block-label_light">
                    {translationOrderId.parameters.fields[paramKey as keyof IPageOrderIdTranslation['parameters']['fields']]}
                </div>
                {{
                    [EOrderIdParameterType.autoReport]: renderAutoReport(paramValue as IOrderIdParameterAutoReport),
                    [EOrderIdParameterType.date]: renderDate(paramValue as IOrderIdParameterDate),
                    [EOrderIdParameterType.datePeriod]: renderDatePeriod(paramValue as IOrderIdParameterDatePeriod),
                    [EOrderIdParameterType.link]: renderLink(paramValue as IOrderIdParameterLink),
                    [EOrderIdParameterType.list]: renderList(paramValue as IOrderIdParameterList),
                    [EOrderIdParameterType.text]: renderText(paramValue as IOrderIdParameterCommon),
                }[type]}
            </div>
        );
    }, [renderAutoReport, renderDate, renderDatePeriod, renderLink, renderList, renderText, translationOrderId]);

    return (
        <Fragment>
            <div className="order__sub-title order__block-label order__block-label_bold">
                {translationOrderId.parameters.title}
            </div>
            {Object.entries(parameters).map(renderParameter)}
        </Fragment>
    );
};

export default OrderIdParameters;
